import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import DetailWrapper from "../../detail/DetailWrapper";
import withMeta from '../../utils/withMeta';
import { parseValue, processAfter, processBefore, isInvalid as isInvalidHelper } from '../../utils/helper';
import Button from "../../buttons/Button";
import ConfirmDialog from "../../dialogs/ConfirmDialog";
import LoadingPanel from "../../LoadingPanel";
import { useDidMount } from '../../utils/hooks';
import reducer from '../reducer';
import componentFactory from '../componentFactory';
import { showMsg } from '../../utils/logger';

const frmPrimaryField = "IUz";

const UzDetail = (props) => {
  const { refresh, setUz, uz, db, meta } = props;

  const [state, setState] = useState({
    isLoading: true,
    detailSchema: [],
    ui: null,
    detailData: null,
    isOpenConfirmDialog: false
  });

  const detailRef = useRef(null);

  const getReducerState = async (action, data, newState) => {
    const reducerState = await reducer(action, { ...data, obj: {} }, { ...newState, frmPrimaryField }, state, null)
    return reducerState
  }

  const getSchema = async () => {
    try {
      const res = await meta.getSchema(frmPrimaryField, db);
      const detailSchema = res.Detail;
      const ui = res.Args?.ui ?? null;
      let detailData = {};
      for (const x of detailSchema) {
        detailData[x.field] = await getValue(x);
      }

      setState((prevState) => ({ ...prevState, detailSchema, ui, detailData: prevState.detailData ? prevState.detailData : detailData, isLoading: false }));
    } catch (error) {
      setState({ ...state, isLoading: false });
    }
  };

  useDidMount(() => {
    getSchema();
  })

  useEffect(() => {
    setState((prevState) => ({ ...prevState, detailData: uz }))
  }, [uz]);

  useEffect(() => {
    const openConfirmDialog = () => {
      setState((prevState) => ({ ...prevState, isOpenConfirmDialog: true }));
    };

    if (state.detailData && refresh) {
      if (_.isEqual(uz, state.detailData)) {
        onNext();
      } else {
        openConfirmDialog();
      }
    }
    // eslint-disable-next-line
  }, [refresh]);

  const getList = async (filter, sort, page, primaryField) => {
    try {
      setState(prevState => ({
        ...prevState,
        [primaryField]: { ...prevState[primaryField], isLoading: true, data: [], total: 0 }
      }));

      const res = await meta.getList(primaryField, filter, sort, page, db);

      setState(prevState => ({
        ...prevState,
        [primaryField]: { ...prevState[primaryField], isLoading: false, data: res.Data.Items, total: res.Data.TotalRecords }
      }));

      props.getListSuccess(res);
    } catch (error) {
      setState(prevState => ({
        ...prevState,
        [primaryField]: { ...prevState[primaryField], isLoading: false, data: [], total: 0 }
      }));

      props.getListFailure(error);
    }
  };

  const onCreate = async (d) => {
    let detailData = processBefore(state.detailSchema, d);
    detailData[frmPrimaryField] = 0;

    try {
      setState({ ...state, isLoading: true });

      const res = await meta.create(frmPrimaryField, detailData, db);
      res.Data = processAfter(state.detailSchema, res.Data);

      setState({ ...state, detailData: res.Data, isLoading: false });
      props.createSuccess(res);

      setUz(res.Data);
    } catch (error) {
      setState({ ...state, isLoading: false });
      props.createFailure(error);
    }
  };

  const onUpdate = async (id, d) => {
    if (_.isEqual(uz, d)) {
      setUz(uz);
      return;
    }

    let detailData = processBefore(state.detailSchema, d);
    try {
      setState({ ...state, isLoading: true });

      const res = await meta.update(frmPrimaryField, id, detailData, db);
      res.Data = processAfter(state.detailSchema, res.Data);

      setState({ ...state, detailData: res.Data, isLoading: false });

      props.updateSuccess(res);
      setUz(res.Data);
    } catch (error) {
      setState({ ...state, isLoading: false });
      props.updateFailure(error);
    }
  };

  const getPickerValue = async (primaryField, defaultValue) => {
    const id = parseValue(defaultValue, 'container');
    if (id === parseInt(id, 10)) {
      const res = await meta.get(primaryField, id, db);
      if (res && res.Data) {
        return res.Data;
      }
    }
    return null;
  };

  const getValue = async (x) => {
    const { defaultValue } = x;

    switch (x.type) {
      case 'container':
        return defaultValue ? await getPickerValue(x.primaryField, defaultValue) : null;
      case 'boolean':
        return parseValue(defaultValue, "boolean") || false;
      default:
        return parseValue(defaultValue, x.type);
    }
  };

  const onChange = async (name, value) => {
    const oldValue = _.get(state.detailData, name);
    const newState = { primaryField: frmPrimaryField, [frmPrimaryField]: { detailData: {...state.detailData, [name]: value}} };
    const reducerState = await getReducerState('CHANGE', { field: name, value: value, oldValue: oldValue }, newState);
    setState(prev => ({ ...prev, ...reducerState[frmPrimaryField] }))
  };

  const onNext = () => {
    detailRef.current.handleSubmit(new Event('submit'));
  };


  const onCloseConfirmDialog = () => {
    setState((prevState) => ({ ...prevState, isOpenConfirmDialog: false }));
  };

  const onConfirmDialog = async () => {
    onCloseConfirmDialog();

    if (isDefaultValid()) {
      setUz(uz);
      return;
    }

    let detailData = {};
    for (const x of state.detailSchema) {
      detailData[x.field] = await getValue(x);
    }
    if (uz) {
      detailData = uz;
    }

    setState((prevState) => ({ ...prevState, detailData }), onNext);
  };

  const isDefaultValid = () => {
    if (!uz) {
      return false;
    }

    let submit = true;
    for (let field of state.detailSchema) {
      const value = uz[field.field];

      if (isInvalid(field, value)) {
        return false;
      }
    };
    return submit;
  };

  const isInvalid = (field, value) => {
    if (field.disabled) {
      return false;
    }

    const inEdit = uz["IUz"] !== null;
    return isInvalidHelper(field, value, inEdit);
  };

  if (state.isLoading) {
    return <LoadingPanel isLoading={state.isLoading} />;
  }

  return (
    <>
      <DetailWrapper
        ref={detailRef}
        primaryField={frmPrimaryField}
        schema={state.detailSchema}
        data={state.detailData}
        ui={state.ui}
        parentProps={{ ...state, getList }}
        onChange={onChange}
        onCreate={onCreate}
        isLoading={state.isLoading}
        allData={[]}
        onGet={() => { }}
        onUpdate={onUpdate}
        onSubmit={() => { }}
        toolbarHidden={true}
        withMeta
        componentFactory={(primaryField, field, value, onChange, obj) => componentFactory(null, primaryField, field, value, onChange, { ...obj, showMsg: props.showMsg })}
      />
      <Button
        buttonWrapperClassName={"next-button"}
        label={"Zaevidovať používateľa"}
        onClick={onNext}
        primary
      />
      <ConfirmDialog
        isOpen={state.isOpenConfirmDialog}
        onClose={onCloseConfirmDialog}
        onConfirm={onConfirmDialog}
        text={"Máte rozpracovaný záznam. Chcete zahodiť zmeny?"}
        confirmText="Áno"
        cancelText="Nie"
      />
    </>
  );

}

UzDetail.propTypes = {
  refresh: PropTypes.number,
  setUz: PropTypes.func.isRequired,
  uz: PropTypes.object,
  db: PropTypes.string
}

const mapStateToProps = () => ({
})

const mapDispatchToProps = dispatch => ({
  showMsg: (msg, msgType = 'ok', isDialog = false, title) => dispatch(showMsg(msg, msgType, isDialog, title)),
  getListSuccess: res => dispatch({ type: 'GET_LIST_SUCCESS', res }),
  getListFailure: error => dispatch({ type: 'GET_LIST_FAILURE', error }),
  createSuccess: res => dispatch({ type: 'CREATE_SUCCESS', res }),
  createFailure: error => dispatch({ type: 'CREATE_FAILURE', error }),
  updateSuccess: res => dispatch({ type: 'UPDATE_SUCCESS', res }),
  updateFailure: error => dispatch({ type: 'UPDATE_FAILURE', error })
})

export default withMeta(connect(
  mapStateToProps,
  mapDispatchToProps
)(UzDetail));